import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "../Assets/Css/HumimicLoader.css";

export default function HumimicLoader({
  size = "105px",
  mode = "signature",
  theme = "light",
  className = "",
  style = {},
  ariaLabel = "HumimicAI loading",
}) {
  const loaderRef = useRef(null);

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;

    const computeSize = (w) => {
      if (w < 60) return "xs";
      if (w < 90) return "s";
      if (w < 135) return "m";
      return "l";
    };

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      el.dataset.s = computeSize(rect.width || parseInt(size, 10) || 140);
    };

    updateSize();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(([entry]) => {
        if (entry && entry.contentRect) {
          el.dataset.s = computeSize(entry.contentRect.width);
        }
      });
      ro.observe(el);
    }

    return () => {
      if (ro) ro.disconnect();
    };
  }, [size]);

  return (
    <div className="humimic-loader-wrapper">
      <div
        ref={loaderRef}
        className={`humimic-loader ${theme === "dark" || theme === "onDark" ? "theme-dark" : ""} ${className}`}
        data-mode={mode}
        data-theme={theme}
        data-state="visible"
        role="img"
        aria-busy="true"
        aria-label={ariaLabel}
        style={{ "--size": typeof size === "number" ? `${size}px` : size, ...style }}
      >
        <svg
          viewBox="0 0 245 281"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <g id="humimic-n">
              <path d="M159 193L154 186L138 175L126 173L80 204L72 206L29 177L24 176L20 179L20 225L23 230L64 258L72 261L82 257L156 207L159 202Z" />
              <path d="M68 20L23 50L20 55L20 159L24 164L68 194L74 194L77 190L77 25L75 21Z" />
            </g>
            <g id="humimic-r">
              <path d="M170 89L167 92L167 257L174 261L224 227L225 127L220 120L177 90Z" />
              <path d="M225 56L220 49L181 22L169 20L90 74L86 79L85 89L91 98L106 108L117 111L162 81L173 78L215 108L222 109L225 105Z" />
            </g>
            <g id="humimic-all">
              <use href="#humimic-n" />
              <use href="#humimic-r" />
            </g>
            <linearGradient id="win">
              <stop stopColor="#000" stopOpacity="0" />
              <stop offset=".10" stopColor="#fff" stopOpacity=".10" />
              <stop offset=".24" stopColor="#fff" stopOpacity=".34" />
              <stop offset=".38" stopColor="#fff" stopOpacity=".78" />
              <stop offset=".50" stopColor="#fff" />
              <stop offset=".62" stopColor="#fff" stopOpacity=".78" />
              <stop offset=".76" stopColor="#fff" stopOpacity=".34" />
              <stop offset=".90" stopColor="#fff" stopOpacity=".10" />
              <stop offset="1" stopColor="#000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="resWin">
              <stop stopColor="#000" stopOpacity="0" />
              <stop offset=".12" stopColor="#fff" stopOpacity=".04" />
              <stop offset=".28" stopColor="#fff" stopOpacity=".16" />
              <stop offset=".50" stopColor="#fff" stopOpacity=".36" />
              <stop offset=".72" stopColor="#fff" stopOpacity=".16" />
              <stop offset=".88" stopColor="#fff" stopOpacity=".04" />
              <stop offset="1" stopColor="#000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="recWin">
              <stop stopColor="#000" stopOpacity="0" />
              <stop offset=".16" stopColor="#fff" stopOpacity=".05" />
              <stop offset=".34" stopColor="#fff" stopOpacity=".18" />
              <stop offset=".50" stopColor="#fff" stopOpacity=".28" />
              <stop offset=".66" stopColor="#fff" stopOpacity=".18" />
              <stop offset=".84" stopColor="#fff" stopOpacity=".05" />
              <stop offset="1" stopColor="#000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="metal" x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="#fff" />
              <stop offset=".35" stopColor="#D7DCE4" />
              <stop offset=".62" stopColor="#fff" />
              <stop offset="1" stopColor="#A8B0BE" />
            </linearGradient>
            <mask
              id="sweepMask"
              maskUnits="userSpaceOnUse"
              x="-150"
              y="-30"
              width="560"
              height="350"
            >
              <rect x="-150" y="-30" width="560" height="350" fill="#000" />
              <g className="sweep">
                <rect
                  className="focus"
                  x="-116"
                  y="-30"
                  width="224"
                  height="350"
                  fill="url(#win)"
                />
              </g>
            </mask>
            <mask
              id="frontMask"
              maskUnits="userSpaceOnUse"
              x="-150"
              y="-30"
              width="560"
              height="350"
            >
              <rect x="-150" y="-30" width="560" height="350" fill="#000" />
              <g className="sweep">
                <rect x="98" y="-30" width="10" height="350" fill="url(#win)" />
              </g>
            </mask>
            <mask
              id="resMask"
              maskUnits="userSpaceOnUse"
              x="-170"
              y="-30"
              width="600"
              height="350"
            >
              <rect x="-170" y="-30" width="600" height="350" fill="#000" />
              <g className="residual">
                <rect
                  x="-130"
                  y="-30"
                  width="244"
                  height="350"
                  fill="url(#resWin)"
                />
              </g>
            </mask>
            <mask
              id="recMask"
              maskUnits="userSpaceOnUse"
              x="-175"
              y="-30"
              width="620"
              height="350"
            >
              <rect x="-175" y="-30" width="620" height="350" fill="#000" />
              <g className="recovery">
                <rect
                  x="-138"
                  y="-30"
                  width="236"
                  height="350"
                  fill="url(#recWin)"
                />
              </g>
            </mask>
            <mask
              id="metalMask"
              maskUnits="userSpaceOnUse"
              x="-150"
              y="-30"
              width="560"
              height="350"
            >
              <rect x="-150" y="-30" width="560" height="350" fill="#000" />
              <g className="metal-window">
                <rect x="72" y="-30" width="48" height="350" fill="url(#win)" />
              </g>
            </mask>
            <clipPath id="shape">
              <use href="#humimic-all" />
            </clipPath>
            <clipPath id="inf">
              <rect x="92" y="120" width="66" height="43" rx="9" />
            </clipPath>
            <filter id="haze" x="-25%" y="-25%" width="150%" height="150%">
              <feGaussianBlur stdDeviation="7.2" />
            </filter>
            <filter id="body" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation=".45" />
            </filter>
            <filter id="resHaze" x="-28%" y="-28%" width="156%" height="156%">
              <feGaussianBlur stdDeviation="9.8" />
            </filter>
            <filter id="resSoft" x="-18%" y="-18%" width="136%" height="136%">
              <feGaussianBlur stdDeviation="4.6" />
            </filter>
            <filter id="recovery" x="-15%" y="-15%" width="130%" height="130%">
              <feGaussianBlur stdDeviation="3.2" />
            </filter>
            <filter id="micro" x="-6%" y="-6%" width="112%" height="112%">
              <feGaussianBlur stdDeviation=".34" />
            </filter>
            <filter
              id="infinitySoft"
              x="-180%"
              y="-180%"
              width="460%"
              height="460%"
            >
              <feGaussianBlur stdDeviation="2.6" />
            </filter>
            <filter id="hazeMicro" x="-22%" y="-22%" width="144%" height="144%">
              <feGaussianBlur stdDeviation="5.2" />
            </filter>
            <filter id="hazeSmall" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4.2" />
            </filter>
            <filter id="hazeXS" x="-18%" y="-18%" width="136%" height="136%">
              <feGaussianBlur stdDeviation="3.3" />
            </filter>
            <filter id="bodySmall" x="-8%" y="-8%" width="116%" height="116%">
              <feGaussianBlur stdDeviation=".28" />
            </filter>
            <filter
              id="infinitySmall"
              x="-150%"
              y="-150%"
              width="400%"
              height="400%"
            >
              <feGaussianBlur stdDeviation="1.7" />
            </filter>
          </defs>
          <g className="navy">
            <use href="#humimic-n" />
          </g>
          <g className="rose">
            <use href="#humimic-r" />
          </g>
          <g mask="url(#recMask)">
            <g className="recover-n">
              <use href="#humimic-n" />
            </g>
            <g className="recover-r">
              <use href="#humimic-r" />
            </g>
          </g>
          <g mask="url(#resMask)">
            <g className="res-haze">
              <use href="#humimic-all" />
            </g>
            <g className="res-body">
              <use href="#humimic-all" />
            </g>
          </g>
          <g mask="url(#sweepMask)">
            <g className="hue-haze">
              <use href="#humimic-all" />
            </g>
            <g className="hue-solid">
              <use href="#humimic-all" />
            </g>
            <g className="hue-body">
              <use href="#humimic-all" />
            </g>
          </g>
          <g mask="url(#frontMask)" clipPath="url(#shape)">
            <g className="front-seam">
              <use href="#humimic-all" />
            </g>
          </g>
          <g mask="url(#metalMask)" clipPath="url(#shape)">
            <g className="recess">
              <use href="#humimic-all" />
            </g>
            <g className="metal">
              <use href="#humimic-all" />
            </g>
            <g className="catch">
              <circle cx="74" cy="24" r="2.1" />
              <circle cx="88" cy="83" r="1.8" />
              <circle cx="173" cy="79" r="1.8" />
              <circle cx="221" cy="106" r="1.9" />
              <circle cx="157" cy="201" r="1.8" />
              <circle cx="73" cy="258" r="2" />
            </g>
          </g>
          <g mask="url(#sweepMask)" clipPath="url(#inf)">
            <path
              className="infinity-soft"
              d="M99 136C106 125 116 126 124 139C132 152 142 153 150 140C142 127 132 128 124 140C116 153 106 153 99 142C95 136 96 132 99 136"
            />
            <path
              className="infinity-core"
              d="M99 136C106 125 116 126 124 139C132 152 142 153 150 140C142 127 132 128 124 140C116 153 106 153 99 142C95 136 96 132 99 136"
            />
          </g>
          <g clipPath="url(#inf)">
            <path
              className="infinity-resolve"
              pathLength="1"
              d="M99 136C106 125 116 126 124 139C132 152 142 153 150 140C142 127 132 128 124 140C116 153 106 153 99 142C95 136 96 132 99 136"
            />
          </g>
        </svg>
        <div className="track" aria-hidden="true">
          <span className="seg res"></span>
          <span className="seg main">
            <span className="packets">
              <span className="packet a"></span>
              <span className="packet b"></span>
              <span className="packet merge">
                <span className="head"></span>
              </span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

HumimicLoader.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mode: PropTypes.oneOf(["signature", "micro"]),
  className: PropTypes.string,
  style: PropTypes.object,
  ariaLabel: PropTypes.string,
};
